// Branding Settings Page - Portal customization for end users
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  Avatar,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Palette,
  Save,
  Image as ImageIcon,
  Upload,
  Delete,
  OpenInNew,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/contexts/ToastContext';
import { usePermissions } from '@/hooks/usePermissions';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/services/firebase/config';

const brandingSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color'),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').optional().or(z.literal('')),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').optional().or(z.literal('')),
  portalTitle: z.string().min(1, 'Portal title is required'),
  portalDescription: z.string().optional(),
  welcomeMessage: z.string().optional(),
  footerText: z.string().optional(),
  showPoweredBy: z.boolean(),
});

type BrandingFormData = z.infer<typeof brandingSchema>;

// Color picker component
const ColorPicker = ({
  value,
  onChange,
  label,
  disabled,
}: {
  value: string;
  onChange: (color: string) => void;
  label: string;
  disabled?: boolean;
}) => {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: value || '#e2e8f0',
            border: '2px solid',
            borderColor: 'divider',
            cursor: disabled ? 'default' : 'pointer',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.2s',
            '&:hover': disabled ? {} : { transform: 'scale(1.05)' },
          }}
        >
          <input
            type="color"
            value={value || '#2563eb'}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            title={label}
            aria-label={label}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: disabled ? 'default' : 'pointer',
            }}
          />
        </Box>
        <TextField
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          disabled={disabled}
          placeholder="#2563eb"
          sx={{ width: 120 }}
          inputProps={{ style: { fontFamily: 'monospace' } }}
        />
      </Box>
    </Box>
  );
};

export const BrandingSettingsPage = () => {
  const navigate = useNavigate();
  const { organization, refreshOrganization } = useOrganization();
  const { showSuccess, showError } = useToast();
  const { can, plan } = usePermissions();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canCustomize = plan !== 'free'; // Only paid plans can customize branding
  const canRemoveBadge = plan === 'business' || plan === 'enterprise';

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<BrandingFormData>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {
      primaryColor: '#2563eb',
      secondaryColor: '',
      accentColor: '',
      portalTitle: 'Help Center',
      portalDescription: '',
      welcomeMessage: '',
      footerText: '',
      showPoweredBy: true,
    },
  });

  const primaryColor = watch('primaryColor');
  const showPoweredBy = watch('showPoweredBy');

  // Load organization branding into form
  useEffect(() => {
    if (organization?.branding) {
      const b = organization.branding;
      reset({
        primaryColor: b.primaryColor || '#2563eb',
        secondaryColor: b.secondaryColor || '',
        accentColor: b.accentColor || '',
        portalTitle: b.portalTitle || 'Help Center',
        portalDescription: b.portalDescription || '',
        welcomeMessage: b.welcomeMessage || '',
        footerText: b.footerText || '',
        showPoweredBy: b.showPoweredBy !== false,
      });
      setLogoUrl(b.logoUrl || null);
    }
  }, [organization, reset]);

  const onSubmit = async (data: BrandingFormData) => {
    if (!organization) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, 'organizations', organization.id), {
        'branding.primaryColor': data.primaryColor,
        'branding.secondaryColor': data.secondaryColor || null,
        'branding.accentColor': data.accentColor || null,
        'branding.portalTitle': data.portalTitle,
        'branding.portalDescription': data.portalDescription || null,
        'branding.welcomeMessage': data.welcomeMessage || null,
        'branding.footerText': data.footerText || null,
        'branding.showPoweredBy': canRemoveBadge ? data.showPoweredBy : true,
        'branding.logoUrl': logoUrl || null,
        updatedAt: serverTimestamp(),
      });

      await refreshOrganization();
      showSuccess('Branding settings saved successfully!');
    } catch (err) {
      console.error('Error saving branding:', err);
      showError('Failed to save branding. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !organization) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      showError('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showError('Logo must be less than 2MB');
      return;
    }

    setUploading(true);
    try {
      const storageRef = ref(storage, `organizations/${organization.id}/logo-${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setLogoUrl(url);
      showSuccess('Logo uploaded successfully!');
    } catch (err) {
      console.error('Error uploading logo:', err);
      showError('Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!logoUrl || !organization) return;

    try {
      // Try to delete from storage (may fail if URL is external)
      try {
        const storageRef = ref(storage, logoUrl);
        await deleteObject(storageRef);
      } catch (e) {
        // Ignore storage errors - file might not exist or be external
      }
      setLogoUrl(null);
      showSuccess('Logo removed');
    } catch (err) {
      console.error('Error deleting logo:', err);
      showError('Failed to remove logo');
    }
  };

  if (!organization) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canEdit = can('org:settings');

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Branding
        </Typography>
        <Typography color="text.secondary">
          Customize how your support portal looks to your customers
        </Typography>
      </Box>

      {!canCustomize && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/', { state: { scrollTo: 'pricing' } })}>
              Upgrade
            </Button>
          }
        >
          Custom branding is available on Pro plans and above. Upgrade to customize colors, logo, and more.
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Left Column - Settings */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Stack spacing={3}>
              {/* Colors */}
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Palette sx={{ color: 'white' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        Colors
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose colors that match your brand
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Controller
                        name="primaryColor"
                        control={control}
                        render={({ field }) => (
                          <ColorPicker
                            value={field.value}
                            onChange={field.onChange}
                            label="Primary Color"
                            disabled={!canEdit || !canCustomize}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Controller
                        name="secondaryColor"
                        control={control}
                        render={({ field }) => (
                          <ColorPicker
                            value={field.value || ''}
                            onChange={field.onChange}
                            label="Secondary Color"
                            disabled={!canEdit || !canCustomize}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Controller
                        name="accentColor"
                        control={control}
                        render={({ field }) => (
                          <ColorPicker
                            value={field.value || ''}
                            onChange={field.onChange}
                            label="Accent Color"
                            disabled={!canEdit || !canCustomize}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Logo Upload */}
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ImageIcon sx={{ color: 'white' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        Logo
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Upload your company logo for the portal header
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {logoUrl ? (
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          src={logoUrl}
                          variant="rounded"
                          sx={{ width: 80, height: 80 }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleLogoDelete}
                          disabled={!canEdit || !canCustomize}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 2,
                          border: '2px dashed',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f8fafc',
                        }}
                      >
                        <ImageIcon sx={{ color: 'text.disabled', fontSize: 32 }} />
                      </Box>
                    )}

                    <Box>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleLogoUpload}
                        accept="image/*"
                        title="Upload logo image"
                        aria-label="Upload logo image"
                        style={{ display: 'none' }}
                      />
                      <Button
                        variant="outlined"
                        startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!canEdit || !canCustomize || uploading}
                      >
                        {uploading ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Recommended: 200x200px, PNG or SVG, max 2MB
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Portal Text */}
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                    Portal Content
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        {...register('portalTitle')}
                        label="Portal Title"
                        fullWidth
                        error={!!errors.portalTitle}
                        helperText={errors.portalTitle?.message}
                        disabled={!canEdit || !canCustomize}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        {...register('portalDescription')}
                        label="Portal Description"
                        fullWidth
                        disabled={!canEdit || !canCustomize}
                        placeholder="Find answers and get support"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        {...register('welcomeMessage')}
                        label="Welcome Message"
                        fullWidth
                        multiline
                        rows={2}
                        disabled={!canEdit || !canCustomize}
                        placeholder="How can we help you today?"
                        helperText="Shown at the top of your support portal"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        {...register('footerText')}
                        label="Footer Text"
                        fullWidth
                        disabled={!canEdit || !canCustomize}
                        placeholder="© 2024 Your Company. All rights reserved."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Powered By Badge */}
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        "Powered by SupportSpace" Badge
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Show or hide the SupportSpace branding in your portal footer
                      </Typography>
                      {!canRemoveBadge && (
                        <Chip
                          label="Business plan required"
                          size="small"
                          sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                        />
                      )}
                    </Box>
                    <Controller
                      name="showPoweredBy"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                              disabled={!canEdit || !canRemoveBadge}
                            />
                          }
                          label={field.value ? 'Visible' : 'Hidden'}
                          labelPlacement="start"
                        />
                      )}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Save Button */}
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                {!canEdit && (
                  <Alert severity="info" sx={{ flex: 1 }}>
                    You don't have permission to edit branding settings.
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canEdit || saving || (!isDirty && !logoUrl)}
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  sx={{
                    px: 4,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                    },
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Stack>
          </Grid>

          {/* Right Column - Preview Card */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    PREVIEW
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<OpenInNew />}
                    href={`/p/${organization.slug}`}
                    target="_blank"
                  >
                    View Portal
                  </Button>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {/* Mini portal preview */}
                  <Box
                    sx={{
                      bgcolor: primaryColor,
                      color: 'white',
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                      textAlign: 'center',
                    }}
                  >
                    {logoUrl ? (
                      <Avatar src={logoUrl} sx={{ width: 40, height: 40, mx: 'auto', mb: 1 }} />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          mx: 'auto',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {organization.name[0]}
                      </Box>
                    )}
                    <Typography variant="subtitle2" fontWeight="bold">
                      {watch('portalTitle') || 'Help Center'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ height: 8, bgcolor: '#e2e8f0', borderRadius: 1, width: '100%' }} />
                    <Box sx={{ height: 8, bgcolor: '#e2e8f0', borderRadius: 1, width: '80%' }} />
                    <Box sx={{ height: 8, bgcolor: '#e2e8f0', borderRadius: 1, width: '60%' }} />
                  </Box>
                  {showPoweredBy && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                    >
                      Powered by SupportSpace
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
